export const ONLINE_LABELS = ["online", "online event"]

export type MapCoordinates = { lat: number; lng: number }

/**
 * #1400 — picks the coordinates to pin on an event's map, or signals that no
 * pin should render. The only trustworthy source is the event's own stored
 * `latitude`/`longitude` (captured from the Mapbox picker at create/edit,
 * #748) — a fresh free-text geocode of the address string is not reliable
 * enough to pin unsupervised (it can resolve to an unrelated place) and must
 * never be used as a silent fallback. When the event has no stored
 * coordinates, this returns null so the caller renders the address text with
 * no pin, rather than guessing.
 */
export const resolveEventMapCoordinates = (
  latitude?: number | null,
  longitude?: number | null,
): MapCoordinates | null => {
  if (typeof latitude !== "number" || typeof longitude !== "number") return null
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null
  return { lat: latitude, lng: longitude }
}

export const isOnlineLocation = (location?: string | null): boolean => {
  if (!location) return true
  const normalized = location.trim().toLowerCase()
  return !normalized || ONLINE_LABELS.includes(normalized)
}

export const summarizeLocation = (location?: string | null): string => {
  if (!location) return "Online Event"

  const trimmed = location.trim()
  if (!trimmed) return "Online Event"

  if (isOnlineLocation(trimmed)) {
    return "Online Event"
  }

  const parts = trimmed.split(",").map((part) => part.trim()).filter(Boolean)

  if (parts.length === 0) return trimmed

  const country = parts[parts.length - 1] ?? ""
  const suburb = parts.length > 1 ? parts[parts.length - 2] ?? "" : (parts[0] ?? "")

  if (!country && !suburb) return trimmed
  if (!country) return suburb
  if (!suburb) return country
  if (suburb === country) return suburb

  return `${suburb}, ${country}`
}

